import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { invalidatePlanningOverride } from './invalidateForecastViews';
import type {
  ListInventoryPolicyOverridesInput,
  ListInventoryPolicyOverridesResponse,
  InventoryPolicyOverrideRow,
  EffectivePolicy,
  ListPolicyOverrideCombosResponse,
  PolicyOverrideComboRow,
} from '@erp/rpc';

export type {
  ListInventoryPolicyOverridesResponse,
  InventoryPolicyOverrideRow,
  EffectivePolicy,
  ListPolicyOverrideCombosResponse,
  PolicyOverrideComboRow,
};

// All overrides (active + historical) for a policy + its resolved effective
// values. The policy is addressed by its SKU at a warehouse; ProductV3 has no
// variant dimension.
export function useInventoryPolicyOverrides(
  input: ListInventoryPolicyOverridesInput,
  enabled = true,
) {
  return useQuery<ListInventoryPolicyOverridesResponse>({
    ...orpc.procurement.inventoryPolicyOverrides.listByPolicy.queryOptions({
      input,
    }),
    enabled: enabled && !!input.warehouseId && !!input.productV3Key,
  });
}

// One policy combo per warehouse for a product — the product-detail overrides
// table, addressed by the SKU.
export function useInventoryPolicyOverridesByProduct(
  input: { productV3Key?: string | null },
  enabled = true,
) {
  const { productV3Key } = input;
  return useQuery<ListPolicyOverrideCombosResponse>({
    ...orpc.procurement.inventoryPolicyOverrides.listByProduct.queryOptions({
      input: { productV3Key: productV3Key ?? '' },
    }),
    enabled: enabled && !!productV3Key,
    // No placeholderData: keeping the previous product's rows during a
    // product switch would let the Edit button open the override panel with a
    // mismatched warehouse/variant from the prior product.
  });
}

export function useCreateInventoryPolicyOverride() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.inventoryPolicyOverrides.create.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'policy'),
    }),
  );
}

// Edit = revoke + replacement on the server; the list shows both rows.
export function useUpdateInventoryPolicyOverride() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.inventoryPolicyOverrides.update.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'policy'),
    }),
  );
}

export function useRevokeInventoryPolicyOverride() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.inventoryPolicyOverrides.revoke.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'policy'),
    }),
  );
}
